import React, { memo, useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { Box, Stack, Grid, Heading, Link } from '@chakra-ui/react';
import {
  Icon,
  Loading,
  Unform,
  SelectInput,
  TextInput,
  MaskInput,
  TextAreaInput,
  Button,
  Tooltip,
  BooleanInput,
} from 'components';
import { RouteComponentProps } from 'react-router';
import { FormHandles } from '@unform/core';
import {
  attendanceDeadlineOptions,
  internalAreaOptions,
  modalityOptions,
  assessmentTypesOptions,
} from 'constants/demand';
import { FileInput } from 'components/Unform/FileInput';
import { IClientDemand, IRequestDemands } from 'types/demands';
import { clientDemandsValidationSchema } from 'yupSchemas/demands';
import { formatDateToBr, formatDateToUs } from 'utils/date';
import { openNotification } from 'utils/notification';
import { createClientDemand } from 'services/createDemand';
import { editClientDemand } from 'services/editDemand';
import { routePaths } from 'constants/routes';
import { getClientDemandData, getClientFile, removeClientFile } from 'services/getDemandData';
import { queryToObj } from 'utils/query';
import { FileItem } from 'components/Form/FileField/styles';
import { extname } from 'utils/files';
import { demandStatusValues } from 'constants/demands';
import { format, parse } from 'date-fns';
import Participants from './components/Participants';
import { addParticipant, getParticipants, removeParticipant } from 'services/participants';
import useEventBus from 'hooks/useEventBus';
import { colors } from 'theme/colors';
import styled from 'styled-components';
import { AiOutlineDownload as Download } from 'react-icons/ai';
import { postUserSolicitation } from 'services/userSolicitation';
import { getClientDemandsData, IGCDDParams } from 'services/demandsTable';
import { getLoggedUser } from 'utils/storage';

const ContainerGrid = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  border: '1px solid red !important';

  .buttonCancel {
    &:hover {
      filter: brightness(0.9);
      background-color: #fff;
    }
  }
`;

const Btn = styled.button`
  background-color: transparent;
`;

export interface Participant {
  id: number;
  name: string;
  email?: string;
  enabled?: boolean;
}

interface IUserType {
  admin: boolean;
  id: number;
  name: string;
}

interface IProps extends RouteComponentProps<{ id?: string }> {}

const NewDemandComp: React.FC<IProps> = ({ history, match }) => {
  const eventBus = useEventBus();
  const demandID = useMemo(() => match.params?.id || false, [match.params]);
  useEffect(() => {
    eventBus.dispatchEvent('onChangeTitle', demandID ? 'Editar solicitação' : 'Nova solicitação');
  }, [demandID]);
  const serviceID = useMemo(() => {
    return queryToObj(history.location.search).serviceType as string | undefined;
  }, [history.location.search]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [demand, setDemand] = useState<IClientDemand>();
  const formRef = useRef<FormHandles>(null);
  const [isSla, setIsSla] = useState(false);
  const [isC, setIsC] = useState(serviceID === '2');
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [savedParticipants, setSavedParticipants] = useState<Participant[]>([]);

  const [deletedParticipants, setDeletedParticipants] = useState<Participant[]>([]);
  const parRef = useRef({
    participants,
    deletedParticipants,
  });

  const handleGetData = useCallback(async () => {
    if (!demandID) return;
    try {
      setIsLoading(true);
      const response = await getClientDemandData(demandID);
      const dm = {
        ...response.data,
        solicitacaoDocumentos: response.data.solicitacaoDocumentos
          ? response.data.solicitacaoDocumentos.map(i => ({ ...i, ext: extname(i.filename) }))
          : [],
        prazo: formatDateToBr(response.data.prazo.replace(/T\S+/, '')),
        serviceType: response.data.serviceType.id,
        createdAt: response.data.createdAt
          ? format(parse(response.data.createdAt, 'yyyy-MM-dd', Date.now()), 'dd/MM/yyyy')
          : '',
      };
      if (dm.status !== demandStatusValues.pending) return history.goBack();
      setDemand(dm);
      // statusRef.current = response.data.status;
      if (response.data.prazoAtendimento === 'SLA') {
        setIsSla(true);
      } else {
        setIsSla(false);
      }
      const { data: dt } = await getParticipants(demandID);
      setSavedParticipants(
        dt.map(i => ({
          id: i.id,
          name: i.user.name,
          email: i.user.email,
          enabled: i.user.enabled,
        })),
      );
      setIsC(parseInt(dm.serviceType + '') === 2);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [demandID]);

  const getUserType = () => {
    const userType = localStorage.getItem('@dpohub/user');

    if (typeof userType === 'string') {
      const userTp: IUserType = JSON.parse(userType);

      return {
        admin: userTp.admin,
        id: userTp.id,
        name: userTp.name,
      };
    }
  };

  useEffect(() => {
    getUserType();
    handleGetData();
  }, [handleGetData]);

  useEffect(() => {
    parRef.current.participants = participants;
    parRef.current.deletedParticipants = deletedParticipants;
  }, [participants, deletedParticipants]);

  const handleIsC = useCallback(({ target: { value } }: React.ChangeEvent<HTMLSelectElement>) => {
    setIsC(parseInt(value) === 2);
    formRef.current?.clearField('tipoConsulta');
    formRef.current?.clearField('tipoAvaliacao');
  }, []);

  const handleSubmit = useCallback(
    async (formData: IRequestDemands) => {
      try {
        if ([...(formData.files || []), ...(demand?.solicitacaoDocumentos || [])].length > 10) {
          return openNotification('Quantia maxima de 10 arquivos por solicitação', 'error');
        }

        setIsSaving(true);

        const createData = {
          ...formData,
          prazo:
            formData.prazoAtendimento === 'SLA' ? undefined : formatDateToUs(formData.prazo || ''),
          serviceType: {
            id: parseInt(formData.serviceType + ''),
          },
          files: undefined,
          status: demand?.status || undefined,
          createdAt: demand?.createdAt ? formatDateToUs(demand?.createdAt) : undefined,
          // status: statusRef.current || undefined,
        };

        delete createData.files;
        if (!createData.tipoAvaliacao) createData.tipoAvaliacao = null;
        if (!createData.tipoConsulta) createData.tipoConsulta = null;
        if (!createData.prazo) delete createData.prazo;
        if (!createData.status || !demandID) delete createData.status;
        let solId: number | string | boolean = demandID;
        if (!demandID) {
          const fd = new FormData();
          fd.append(
            'documentosConsultasDto',
            new Blob([JSON.stringify(createData)], { type: 'application/json' }),
          );
          if (formData.files) formData.files.forEach(file => fd.append('files', file, file.name));
          const { data: dt } = await createClientDemand(fd);
          solId = dt.id as number;

          const queryData = {
            size: 10,
            page: 0,
            sort: 'prazo,asc',
          } as IGCDDParams;

          const response = await getClientDemandsData(queryData);

          const user = getUserType();

          await postUserSolicitation({
            id: 1,
            admin: typeof user?.admin === 'boolean' && user.admin,
            name: typeof user?.name === 'string' && user.name,
            userId: typeof user?.id === 'number' && user.id,
            solicitacaoId: response.data.content.length,
          });
        } else {
          // await editClientDemand(demandID, createData);
          const fd = new FormData();
          fd.append(
            'documentosConsultasDto',
            new Blob([JSON.stringify(createData)], { type: 'application/json' }),
          );

          if (formData.files) formData.files.forEach(file => fd.append('files', file, file.name));
          await editClientDemand(demandID, fd);
          openNotification('Demanda editada com sucesso!');
          history.push('/clientDemands?page=0');
          return;
        }

        if (parRef.current.participants.length > 0) {
          for (const participant of parRef.current.participants) {
            await addParticipant({
              solicitacao: {
                id: +solId,
              },
              user: {
                id: participant.id,
              },
            });
          }
        }

        if (parRef.current.deletedParticipants.length > 0) {
          for (const participant of parRef.current.deletedParticipants) {
            await removeParticipant(participant.id);
          }
        }
        const u = getLoggedUser();
        history.push(u.admin ? routePaths.demands : routePaths.clientDemands);

        openNotification('Solicitação criada com sucesso!');
      } catch (error) {
        console.error(error);
      } finally {
        setIsSaving(false);
      }
    },
    [demand?.solicitacaoDocumentos, participants, deletedParticipants],
  );

  const handleDownloadFile = useCallback(async (id: React.ReactText, name: string) => {
    try {
      setIsLoading(true);
      const { data } = await getClientFile(id);
      const a = document.createElement('a');
      a.onclick = () => {
        document.body.removeChild(a);
      };

      a.href = URL.createObjectURL(data);
      a.download = name;
      a.rel = 'noopener noreferrer';
      a.target = '_blank';

      document.body.appendChild(a);

      a.click();
    } catch (err) {
      console.error(err);
      openNotification('Ocorreu um erro ao baixar o arquivo', 'error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleRemoveFile = useCallback(
    async (id: React.ReactText) => {
      try {
        setIsLoading(true);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { data } = await removeClientFile(id);
        handleGetData();
      } catch (err) {
        console.error(err);
        openNotification('Ocorreu um erro ao remover o arquivo', 'error');
        setIsLoading(false);
      }
    },
    [handleGetData],
  );

  return (
    <Box w="100%">
      <Box m="0 auto" maxW="50rem" width="100%">
        <Stack direction={['column', 'row']} spacing="1rem" mb="1rem">
          <Link
            onClick={e => {
              e.preventDefault();
              history.goBack();
            }}
            mt="0.5em"
            href="#"
          >
            {/* <Icon size={1.6} name={'arrowLeft'} /> */}
            {/* essa ARROW PARECE UM SINAL DE MENOR */}
          </Link>

          <Heading size="lg" as="h3">
            {demandID ? 'Editar solicitação' : ''}
          </Heading>
        </Stack>
        {demandID && isLoading ? (
          <Loading />
        ) : (
          <Box
            padding="2rem"
            background="#fff"
            borderRadius="10px"
            boxShadow={'0px 0px 15px rgba(0, 0, 0, 0.25);'}
          >
            <Unform
              onSubmit={handleSubmit}
              validationSchema={clientDemandsValidationSchema}
              formRef={formRef}
              initialData={{
                serviceType: serviceID,
                ...demand,
              }}
            >
              <Grid templateColumns="1.5fr 2fr" gridGap="1rem">
                <SelectInput
                  name="serviceType"
                  placeholder="Modalidade"
                  label="Modalidade"
                  optionValue="id"
                  optionLabel="name"
                  onChange={handleIsC}
                  options={modalityOptions}
                  _placeholder={{ color: colors.brand.gray200 }}
                  color={colors.brand.gray300}
                />
                <SelectInput
                  name="tipoConsulta"
                  placeholder="Tipo de Consulta"
                  _placeholder={{ color: colors.brand.gray200 }}
                  color={colors.brand.gray300}
                  label="Tipo de Consulta"
                  optionValue="id"
                  optionLabel="name"
                  options={internalAreaOptions}
                  hidden={!isC}
                />
                <SelectInput
                  name="tipoAvaliacao"
                  placeholder="Tipo de Avaliação"
                  label="Tipo de Avaliação"
                  optionValue="id"
                  optionLabel="name"
                  options={assessmentTypesOptions}
                  hidden={isC}
                />
              </Grid>
              <TextInput
                name="demanda"
                label="Nome da demanda"
                placeholder="Digite aqui..."
                border={`1px solid ${colors.brand.gray100} !important`}
                _placeholder={{ color: colors.brand.gray200 }}
                color={colors.brand.gray300}
              />
              <TextAreaInput
                name="descricaoTrabalho"
                label="Descrição"
                placeholder="Por favor, inclua todas as informações relacionadas ao contrato como por exemplo: tempo de contratação, forma de pagamento, etc."
                border={`1px solid ${colors.brand.gray100} !important`}
                _placeholder={{ color: colors.brand.gray200 }}
                color={colors.brand.gray300}
                fontSize={'14px'}
              />
              <Grid mt="10vpx" templateColumns="repeat(3, 1fr)" gridGap="1rem">
                <Box flex={1}>
                  <SelectInput
                    label="Prazo"
                    name="prazoAtendimento"
                    optionLabel="name"
                    optionValue="id"
                    options={attendanceDeadlineOptions}
                    onChange={() => setIsSla(!isSla)}
                    tip="O prazo SLA é de 5 dias, contudo, o mesmo poderá ser alterado conforme a complexidade de cada solicitação."
                    // tipIconName="exclamation"
                    // tipIconColor={900}
                  />
                </Box>
                <Box
                  display={isSla === false ? 'none !important' : 'block !important'}
                  flex={1}
                  position="relative"
                  top="23px"
                >
                  <MaskInput
                    mask="date"
                    name="prazo"
                    label=""
                    disabled={!isSla}
                    leftIcon="calendar"
                    placeholder="dd/mm/aaaa"
                    border={`1px solid ${colors.brand.gray100} !important`}
                  />
                </Box>
              </Grid>
              <Box mt="12px">
                <Box
                  mb="10px"
                  display="flex"
                  alignItems="center"
                  gridGap="8px"
                  fontSize="14px"
                  fontWeight="bold"
                  color={'#22272D'}
                >
                  Solicitação válida para debitar do banco de horas?
                  <Tooltip
                    iconName="exclamation"
                    tip="As horas despendidas para execução da demanda serão debitadas do seu banco de horas"
                  />
                </Box>
                {/* <Switch name="consumoHoras" label="SIM" /> */}
                <BooleanInput name="consumoHoras" />
              </Box>
              <Box mt="30px">
                <FileInput
                  label="Upload de documentos"
                  name="files"
                  // placeholder="Arraste ou clique aqui para carregar os arquivos"
                  placeholder="Arraste seu arquivos, ou clique aqui para enviá-los"
                  placeholderType="(imagens, vídeos, documentos e etc.)"
                  _placeholder={{ color: ` ${colors.brand.black700} !important` }}
                  style={{ color: ` ${colors.brand.black700} !important` }}
                />
                {!!demand?.solicitacaoDocumentos && demand?.solicitacaoDocumentos.length > 0 && (
                  <Box mt="-18px" mb="18px">
                    {demand.solicitacaoDocumentos.map((i, k) => (
                      <FileItem key={k}>
                        <a
                          href="#"
                          onClick={e => {
                            e.preventDefault();
                            handleDownloadFile(i.id, i.filename);
                          }}
                        >
                          <p>
                            <span>
                              <Download color="blue500" fontSize={'25px'} />
                            </span>
                            {i.filename}
                          </p>
                        </a>
                        <Btn onClick={() => handleRemoveFile(i.id)} type="button">
                          <Icon name="trashed" size={1} color="red200" />
                        </Btn>
                      </FileItem>
                    ))}
                  </Box>
                )}
              </Box>
              <Participants
                {...{
                  participants,
                  setParticipants,
                  setSavedParticipants,
                  setDeletedParticipants,
                  savedParticipants,
                }}
              />
              <ContainerGrid>
                <Button
                  className="buttonCancel"
                  style={{
                    border: `2px solid ${colors.brand.tertiary}`,
                    color: colors.brand.tertiary,
                  }}
                  type="reset"
                  onClick={() => {
                    setTimeout(() => {
                      history.goBack();
                    }, 600);
                  }}
                  variant="cancel"
                >
                  Cancelar
                </Button>
                <Button type="submit" isLoading={isSaving}>
                  Concluir
                </Button>
              </ContainerGrid>
            </Unform>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const NewClientDemand = memo(NewDemandComp);
